// ============================================
// GO2 DIGITAL - Content Data
// ============================================

export const navLinks = [
  { label: 'ABOUT', href: '/about/' },
  { label: 'SERVICES', href: '/services/' },
  { label: 'PROJECTS', href: '/projects/' },
];

// HERO SECTION
export const heroVariations = [
  {
    line1: 'We Don\'t',
    line2: 'Market.',
    accent: 'We Build.',
  },
  {
    line1: 'Digital',
    line2: 'Platforms.',
    accent: 'Built to Perform.',
  },
  {
    line1: 'From Zero',
    line2: 'to Revenue.',
    accent: 'We Build the Machine.',
  },
];

export const heroSubheadline = 'go2digital builds, owns, and scales high-performance digital platforms — powered by SEO, affiliate ecosystems, and AI.';

export const heroStats = [
  { value: '500K+', label: 'Monthly Organic Visits' },
  { value: '5', label: 'Live Platforms' },
  { value: '3', label: 'Markets' },
];

// WHAT WE DO
export const whatWeDo = [
  {
    icon: 'Search',
    title: 'SEO & Organic Growth',
    description: 'We engineer search visibility at scale. Our platforms rank for thousands of high-intent keywords, driving consistent organic traffic that compounds over time.',
  },
  {
    icon: 'Link',
    title: 'Affiliate & Monetization',
    description: 'Every platform we build has revenue DNA. We design affiliate ecosystems, lead generation funnels, and partnership models that turn traffic into income.',
  },
  {
    icon: 'Code',
    title: 'Platform Development',
    description: 'We build the infrastructure. Custom platforms, content engines, and scalable web properties designed for long-term growth — not short-term campaigns.',
  },
  {
    icon: 'Brain',
    title: 'AI Integration',
    description: 'We embed AI into our platforms to automate content, personalize user experiences, and create tools that deliver real value — like our AI travel assistant.',
  },
];

// PROJECTS / BRANDS
export const projects = [
  {
    name: 'go2georgia.com',
    type: 'AI Travel Platform',
    status: 'Live',
    year: '2024',
    tagline: 'AI-powered travel planning for Georgia',
    description: 'A full-stack travel platform combining curated destination content with an AI-powered trip planner. Built for organic search dominance in the Georgia travel niche. Monetized through affiliate partnerships with hotels, tours, and local operators.',
    outcomes: [
      'Ranked in top 3 for 200+ Georgia travel keywords within 8 months',
      'AI trip planner generates personalized itineraries in seconds',
      'Affiliate revenue growing 40% month-over-month',
    ],
    tags: ['AI', 'SEO', 'Affiliate', 'Travel'],
    link: 'https://go2georgia.com',
  },
  {
    name: 'go2slovenia.com',
    type: 'Authority Travel Site',
    status: 'Live',
    year: '2023',
    tagline: 'The definitive guide to Slovenia',
    description: 'A high-authority content platform covering everything travelers need to know about Slovenia. Built as a long-term SEO asset with deep topical coverage, local expertise, and strategic monetization through tourism partnerships.',
    outcomes: [
      'Domain authority established in competitive Slovenia travel niche',
      'Comprehensive coverage across 50+ destinations',
      'Revenue through hotel affiliates and tour operator partnerships',
    ],
    tags: ['SEO', 'Content', 'Affiliate', 'Travel'],
    link: 'https://go2slovenia.com',
  },
  {
    name: 'potovanja.si',
    type: 'Travel Media Platform',
    status: 'Live',
    year: '2022',
    tagline: 'Slovenia\'s travel media engine',
    description: 'A Slovenian-language travel media platform reaching a broad audience with destination guides, travel tips, and curated recommendations. Designed as a traffic engine that feeds the broader go2digital ecosystem.',
    outcomes: [
      'Dominant organic presence in Slovenian travel search',
      'Content engine producing high-ranking articles at scale',
      'Cross-platform traffic distribution across the ecosystem',
    ],
    tags: ['Media', 'SEO', 'Content', 'Slovenian Market'],
    link: 'https://potovanja.si',
  },
  {
    name: 'avanture.si',
    type: 'Adventure Travel Portal',
    status: 'Live',
    year: '2023',
    tagline: 'Adventure travel, curated',
    description: 'A niche portal focused on adventure and outdoor travel in the Slovenian and Balkan region. Targets a passionate audience segment with high conversion potential for experience-based affiliates and tour operators.',
    outcomes: [
      'Niche authority in adventure travel vertical',
      'High-engagement audience with strong conversion rates',
      'Partnership pipeline with adventure tour operators',
    ],
    tags: ['Niche', 'Adventure', 'Affiliate', 'Outdoor'],
    link: 'https://avanture.si',
  },
  {
    name: 'go2balkans.com',
    type: 'Regional Travel Platform',
    status: 'In Development',
    year: '2025',
    tagline: 'The Balkans, unlocked',
    description: 'The next expansion of the go2 ecosystem. A comprehensive travel platform covering the entire Balkan region — from Croatia to Albania, Serbia to Montenegro. Built on the same playbook that made go2georgia successful.',
    outcomes: [
      'Expanding the go2 blueprint to a massive underserved market',
      'Multi-country content architecture for regional SEO dominance',
      'AI-powered itinerary planning across 10+ Balkan countries',
    ],
    tags: ['Expansion', 'AI', 'SEO', 'Regional'],
    link: null,
  },
];

// SERVICES (detailed for Services page)
export const services = [
  {
    icon: 'Search',
    number: '01',
    title: 'SEO & Organic Growth Systems',
    subtitle: 'Traffic that compounds. Not campaigns that expire.',
    description: 'We don\'t do SEO as a service bolt-on. We build entire platforms engineered for organic search dominance. Deep topical authority, technical excellence, and content systems that produce results for years — not weeks.',
    outcomes: [
      'Sustainable organic traffic that grows without ad spend',
      'Topical authority in specific niches and markets',
      'Content systems that scale without proportional cost increases',
      'First-page rankings for high-commercial-intent keywords',
    ],
    useCases: [
      'go2georgia.com ranked top 3 for 200+ keywords in 8 months',
      'potovanja.si dominates Slovenian travel search organically',
      'go2slovenia.com built authority in competitive travel niche',
    ],
    tags: ['Technical SEO', 'Content Strategy', 'Topical Authority', 'Link Building', 'Search Analytics'],
  },
  {
    icon: 'Link',
    number: '02',
    title: 'Affiliate & Monetization Systems',
    subtitle: 'Revenue infrastructure, not revenue hope.',
    description: 'Every platform we build has monetization baked in from day one. We design affiliate ecosystems, build lead generation funnels, and create partnership models that convert organic traffic into measurable revenue.',
    outcomes: [
      'Diversified revenue streams across multiple affiliate programs',
      'Conversion-optimized user journeys from content to transaction',
      'Partnership frameworks with operators, hotels, and service providers',
      'Revenue attribution and performance tracking at scale',
    ],
    useCases: [
      'go2georgia.com affiliate revenue growing 40% month-over-month',
      'Hotel and tour booking integrations across all travel platforms',
      'Multi-partner affiliate architecture across the ecosystem',
    ],
    tags: ['Affiliate Marketing', 'Lead Generation', 'Revenue Optimization', 'Partnership Models', 'Conversion Funnels'],
  },
  {
    icon: 'Code',
    number: '03',
    title: 'Platform Development',
    subtitle: 'We build the machine. Not just the marketing.',
    description: 'We design and develop custom web platforms built for performance, scale, and long-term value. Not WordPress templates. Not agency projects. Digital assets with real infrastructure, built to operate and grow.',
    outcomes: [
      'Custom platforms optimized for speed, SEO, and user experience',
      'Scalable architecture that handles growth without breaking',
      'Content management systems built for editorial teams',
      'Multi-language, multi-market capability from the ground up',
    ],
    useCases: [
      'go2georgia.com — full-stack platform with AI integration',
      'Multi-platform ecosystem sharing infrastructure and data',
      'Custom CMS powering 5 live platforms simultaneously',
    ],
    tags: ['Web Development', 'Custom Platforms', 'CMS', 'Performance', 'Scalable Architecture'],
  },
  {
    icon: 'Brain',
    number: '04',
    title: 'AI Integration',
    subtitle: 'AI that does work. Not AI for the sake of AI.',
    description: 'We integrate artificial intelligence where it creates real value — automating content workflows, personalizing user experiences, and building tools that make our platforms smarter. Our AI travel assistant is proof it works.',
    outcomes: [
      'AI-powered tools that users actually use and return to',
      'Automated content generation and optimization workflows',
      'Personalized recommendations that increase engagement',
      'Reduced operational costs through intelligent automation',
    ],
    useCases: [
      'go2georgia.com AI trip planner — personalized itineraries in seconds',
      'AI-assisted content creation across the platform network',
      'Planned: AI travel assistant expansion to go2balkans.com',
    ],
    tags: ['AI Tools', 'Automation', 'Personalization', 'Machine Learning', 'Content AI'],
  },
];

// CAPABILITIES (for homepage quick view)
export const capabilities = [
  {
    icon: 'Search',
    title: 'SEO & Organic Growth',
    number: '01',
    description: 'We engineer search visibility at scale. Our platforms rank for thousands of keywords, driving consistent organic traffic that compounds over time.',
    tags: ['Technical SEO', 'Content Strategy', 'Topical Authority'],
  },
  {
    icon: 'Link',
    title: 'Affiliate & Monetization',
    number: '02',
    description: 'Every platform we build has revenue DNA. We design affiliate ecosystems and lead generation funnels that turn traffic into income.',
    tags: ['Affiliate Marketing', 'Lead Generation', 'Revenue Optimization'],
  },
  {
    icon: 'Code',
    title: 'Platform Development',
    number: '03',
    description: 'Custom platforms, content engines, and scalable web properties designed for long-term growth — not short-term campaigns.',
    tags: ['Web Development', 'Custom CMS', 'Performance'],
  },
  {
    icon: 'Brain',
    title: 'AI Integration',
    number: '04',
    description: 'We embed AI into our platforms to automate content, personalize experiences, and build tools that deliver real value.',
    tags: ['AI Tools', 'Automation', 'Personalization'],
  },
];

// TEAM
export const teamMembers = [
  { name: 'Matevz R.', role: 'Founder & CEO', focus: 'VISION & PLATFORM STRATEGY', email: 'matevz' },
  { name: 'Ana K.', role: 'Head of SEO', focus: 'ORGANIC GROWTH & CONTENT', email: 'ana' },
  { name: 'Luka P.', role: 'Lead Developer', focus: 'PLATFORM ARCHITECTURE', email: 'luka' },
  { name: 'Nina S.', role: 'Content Strategist', focus: 'EDITORIAL & PUBLISHING', email: 'nina' },
];

// ABOUT PAGE
export const aboutContent = {
  positioning: 'go2digital is not an agency in the traditional sense. We build, own, and operate digital platforms that generate traffic and revenue. Our clients are our own products — and sometimes, partners who want the same results.',
  story: [
    'go2digital started with a simple thesis: instead of building websites for other people, build platforms we own. Control the traffic. Control the revenue. Control the outcome.',
    'What began as a single travel blog evolved into an ecosystem of five live platforms spanning multiple markets and languages. We learned SEO not from courses — but from ranking our own sites in competitive niches.',
    'Along the way, we developed expertise in affiliate monetization, platform architecture, and AI integration. Not because it was trendy, but because our platforms demanded it.',
    'Today, go2digital operates from Slovenia with a lean team and a focused mission: build digital assets that generate real, measurable value. Every platform in our ecosystem is proof of that mission.',
  ],
  philosophy: {
    title: 'Builder, Not Service Provider.',
    points: [
      { label: 'We eat our own cooking', description: 'Every strategy we use, we test on our own platforms first. If it doesn\'t work for us, we don\'t sell it.' },
      { label: 'Platforms over projects', description: 'We think in terms of assets, not deliverables. A website is not a project — it\'s a machine that should generate value for years.' },
      { label: 'Revenue is the metric', description: 'Traffic without revenue is vanity. Every platform we build is designed to make money — through affiliates, partnerships, or direct monetization.' },
      { label: 'AI is a tool, not a gimmick', description: 'We integrate AI where it creates measurable value. Our travel AI assistant isn\'t a demo — it\'s a product people use.' },
    ],
  },
  advantages: [
    'We operate our own platforms — we know what works because we live it daily',
    'Our SEO expertise is battle-tested across multiple markets and languages',
    'We build for revenue from day one, not as an afterthought',
    'Our AI integration is practical and production-grade, not experimental',
    'We think like owners, not vendors — because we are owners',
  ],
};

// PHILOSOPHY STATS
export const philosophyStats = [
  { value: '5', label: 'Live Platforms' },
  { value: '500K+', label: 'Monthly Organic Visits' },
  { value: '3', label: 'Active Markets' },
];

// CONTACT
export const contactCards = [
  { icon: 'Clock', label: 'AVAILABILITY', value: 'Open for Partners', sub: 'Q3 2025' },
  { icon: 'MapPin', label: 'LOCATION', value: 'Slovenia / Remote', sub: 'CET / Global' },
  { icon: 'Zap', label: 'AVG. RESPONSE', value: '< 24 Hours', sub: '' },
];
